/**
 * Page structure every admin screen shares: a title block, sections, tables, fact lists and the
 * history of staff actions.
 *
 * Below 1024px a table turns each row into a short stack: the first cell on its own line, the rest
 * flowing after it with their column names where a bare value would be ambiguous. Nothing is
 * hidden and nothing scrolls sideways, so who acted and a lab value's reference range stay on
 * screen at tablet and phone widths.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { AdminAuditRow } from "@lattice/core";
import { leadFor, permitFor, useStaffRole } from "./staff.ts";
import { navigate, useScreen } from "./router.ts";
import { action, moment } from "./words.ts";

/* StyleX reads media queries at compile time, so each file declares its own copy. */
const NARROW = "@media (max-width: 1023px)";
const styles = {
  td: {
    k1xSpc: "x1n9xxwz xdxc3v6",
    k8WAf4: "xp1t01x x1ywecm9",
    kg3NbH: "x3i8z2n xopq9sh",
    kt9PQ7: "xso031l x1b6jmyj",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "x1t74ka8",
    kXLuUW: "xxymvpz",
    kLWn49: "xbz0q3o",
    kHjlTd: "x1mzt3pk",
    kcqcaj: "x1l1vl4p",
    $$css: true
  },
  lastRow: {
    kt9PQ7: "x1qhh985",
    $$css: true
  },
  first: {
    kCS8Yb: "x1ofh9rm",
    k63SB2: "xqu410z",
    $$css: true
  },
  wide: {
    kCS8Yb: "x1ofh9rm",
    $$css: true
  },
  numeric: {
    k9WMMc: "xp4054r xovnusx",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  nowrap: {
    khDVqt: "xuxw1ft",
    $$css: true
  }
};
export function PageHead({
  back,
  title,
  lead,
  meta,
  permit,
  actions
}: {
  readonly back?: ReactNode;
  readonly title: ReactNode;
  /** One line: what this screen is. Defaults to the screen's own line. */
  readonly lead?: ReactNode;
  readonly meta?: ReactNode;
  /** Overrides the role line. Omit to use the signed-in role and the current screen. */
  readonly permit?: string;
  readonly actions?: ReactNode;
}) {
  const role = useStaffRole();
  const screen = useScreen();
  const line = permit ?? (role === null ? null : permitFor(role, screen.kind));
  const about = lead === undefined ? leadFor(screen.kind) : lead;
  return <div className="x78zum5 x1a02dak xuk3077 x1qughib x1r0x45p">
      <div className="x78zum5 xdt5ytf x1173whf xeuugli">
        {back === undefined ? null : <div className="xl4dl4e">{back}</div>}
        <h1 className="x1ghz6dp x3cmmws xqu410z x1d3j7mx x1e5flnj x1mzt3pk x1w2vvpw">{title}</h1>
        {about === null ? null : <p className="x1ghz6dp xkyadst x1jty25b xl4dl4e xbz0q3o">{about}</p>}
        {meta === undefined ? null : <div className="x78zum5 x1a02dak x6s0dn4 x1vdzsm xcfm6fe xl4dl4e x1jty25b">{meta}</div>}
        {line === null ? null : <p className="x1ghz6dp xqcrz7y x8hz100 xc7sml1 x1m3fsnl x1890um5 x1jty25b xl4dl4e">{line}</p>}
      </div>
      {actions}
    </div>;
}
export function FilterChip({
  to,
  label,
  count,
  current,
  onClick
}: {
  readonly to: string;
  readonly label: string;
  readonly count?: number;
  readonly current: boolean;
  readonly onClick?: () => void;
}) {
  return <a href={to} aria-current={current ? "true" : undefined} onClick={event => {
    if (onClick) {
      event.preventDefault();
      onClick();
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(to);
  }} {...{
    0: {
      className: "x3nfvp2 x6s0dn4 xskm3c9 x21xpn4 xerji9v x1m3fsnl xmkeg23 x1y0btm7 xtkjqb xcqm4px xzb3lse xl4dl4e xqu410z x1hl2dhg xuxw1ft x171263r"
    },
    1: {
      className: "x3nfvp2 x6s0dn4 xskm3c9 x21xpn4 xerji9v x1m3fsnl xmkeg23 x1y0btm7 xzb3lse xl4dl4e xqu410z x1hl2dhg xuxw1ft x171263r x1890um5 x1uwky97"
    }
  }[!!current << 0]}>
      {label}
      {count === undefined ? null : <span className="x1jty25b x1l1vl4p xox936i">{count}</span>}
    </a>;
}
export function FilterRow({
  children
}: {
  readonly children: ReactNode;
}) {
  return <div className="x78zum5 x1a02dak x1173whf x6s0dn4">{children}</div>;
}
export function Section({
  title,
  count,
  info,
  children
}: {
  readonly title: string;
  readonly count?: number;
  readonly info?: ReactNode;
  readonly children: ReactNode;
}) {
  const id = `section-${title.toLowerCase().replaceAll(/[^a-z]+/g, "-")}`;
  return <section aria-labelledby={id} className="x78zum5 xdt5ytf x626il7 xeuugli">
      <h2 id={id} className="x78zum5 x1a02dak x6s0dn4 x1173whf x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">
        {title}
        {info}
        {count === undefined ? null : <span className="xl4dl4e xox936i x1jty25b x1l1vl4p">{count}</span>}
      </h2>
      {children}
    </section>;
}
export function Panel({
  children,
  padded = false
}: {
  readonly children: ReactNode;
  readonly padded?: boolean;
}) {
  return <div {...{
    0: {
      className: "xcqm4px xmkeg23 x1y0btm7 xtkjqb x1gzjth6 xb3r6kr"
    },
    1: {
      className: "xcqm4px xmkeg23 x1y0btm7 xtkjqb x1gzjth6 xb3r6kr x1wg0vgl"
    }
  }[!!padded << 0]}>{children}</div>;
}

/**
 * A detail page: the record's tables in the main column and its facts and actions in a 340px
 * column beside them. Below 1024px the side column comes first, full width, because at tablet
 * width a fixed side column squeezes the tables until their text breaks a word per line.
 */
export function Split({
  main,
  side
}: {
  readonly main: ReactNode;
  readonly side: ReactNode;
}) {
  return <div className="xrvj5dj x12gnqk0 x1sdzcu3 xy28mw1 xp8ea0k x7a106z x15agyyk">
      <div className="x1qd5a2c x78zum5 xdt5ytf x15agyyk xeuugli">{main}</div>
      <div className="x1xb8wap x78zum5 xdt5ytf x1r0x45p xeuugli">{side}</div>
    </div>;
}

/** What a section shows when it has nothing: one dim line, with no card around nothing. */
function Empty({
  children
}: {
  readonly children: ReactNode;
}) {
  return <p className="x1ghz6dp xq24xx7 xl4dl4e x1jty25b x1jb01v5">{children}</p>;
}
export interface Column<Row> {
  readonly label: string;
  readonly cell: (row: Row) => ReactNode;
  /** Right-aligned with tabular figures, for values a reader compares down the column. */
  readonly numeric?: boolean;
  /** Kept on one line, for short values such as dates that read wrongly when broken. */
  readonly nowrap?: boolean;
  /** On a phone, the column name goes before the value, which alone would be ambiguous. */
  readonly phoneLabel?: boolean;
  /** On a phone, the cell takes a line of its own, as row actions do. */
  readonly wide?: boolean;
}
function cellStyles<Row>(column: Column<Row>, index: number, last: boolean) {
  return stylex.props(styles.td, last && styles.lastRow, index === 0 && styles.first, column.wide === true && styles.wide, column.numeric === true && styles.numeric, column.nowrap === true && styles.nowrap);
}
export function Table<Row>({
  label,
  columns,
  rows,
  rowKey,
  empty,
  selected
}: {
  readonly label: string;
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => string;
  readonly empty: string;
  /** The row an open form below the table is about, shaded so it is clear which one. */
  readonly selected?: (row: Row) => boolean;
}) {
  if (rows.length === 0) return <Empty>{empty}</Empty>;
  return <Panel>
      <table aria-label={label} className="x11r6d5e xdxc3v6 xh8yej3 x1mwwwfo xl4dl4e">
        <thead className="x1whl12z x1a1oztq">
          <tr>
            {columns.map(column => <th key={column.label} scope="col" {...{
            0: {
              className: "x1yc453h xqu410z x1jty25b xp1t01x x3i8z2n xso031l x1q0q8m5 x1t74ka8 xuxw1ft x1l0w1cl"
            },
            1: {
              className: "xqu410z x1jty25b xp1t01x x3i8z2n xso031l x1q0q8m5 x1t74ka8 x1l0w1cl xp4054r xovnusx xuxw1ft"
            }
          }[!!(column.numeric === true) << 0]}>
                {column.label}
              </th>)}
          </tr>
        </thead>
        <tbody className="x5yl94v xdxc3v6">
          {rows.map((row, index) => {
          const last = index === rows.length - 1;
          return <tr key={rowKey(row)} {...{
            0: {
              className: "xf5m44m xbty65p x1a02dak x1pha0wt x1vdzsm xcfm6fe xt970qd xh7goqn xnjsko4 xn892yn x1qhh985 x1ab1ynf x1q0q8m5 x1t74ka8"
            },
            2: {
              className: "xf5m44m xbty65p x1a02dak x1pha0wt x1vdzsm xcfm6fe xt970qd xh7goqn xnjsko4 xn892yn x1q0q8m5 x1t74ka8 x1qhh985"
            },
            1: {
              className: "xf5m44m xbty65p x1a02dak x1pha0wt x1vdzsm xcfm6fe xt970qd xh7goqn xnjsko4 xn892yn x1qhh985 x1ab1ynf x1q0q8m5 x1t74ka8 x1890um5"
            },
            3: {
              className: "xf5m44m xbty65p x1a02dak x1pha0wt x1vdzsm xcfm6fe xt970qd xh7goqn xnjsko4 xn892yn x1q0q8m5 x1t74ka8 x1qhh985 x1890um5"
            }
          }[!!last << 1 | !!(selected?.(row) === true) << 0]}>
                {columns.map((column, at) => <td key={column.label} {...cellStyles(column, at, last)}>
                    {column.phoneLabel === true ? <span className="x1s85apg xs6f2u0 x1jty25b">{column.label} </span> : null}
                    {column.cell(row)}
                  </td>)}
              </tr>;
        })}
        </tbody>
      </table>
    </Panel>;
}
export function Facts({
  items
}: {
  readonly items: readonly (readonly [string, ReactNode])[];
}) {
  return <Panel>
      <dl className="xrvj5dj x16lqc3j x1vdzsm x1xwure6 x1ghz6dp x1wg0vgl xl4dl4e">
        {items.map(([term, value]) => <div key={term} className="xjp7ctv">
            <dt className="x1jty25b">{term}</dt>
            <dd className="x1ghz6dp xj0a0fe x1l1vl4p">{value}</dd>
          </div>)}
      </dl>
    </Panel>;
}

/** Staff actions, newest first: what was done and by whom, when, and the reason they gave. */
export function History({
  rows,
  empty
}: {
  readonly rows: readonly AdminAuditRow[];
  readonly empty: string;
}) {
  if (rows.length === 0) return <Empty>{empty}</Empty>;
  return <Panel>
      <ol className="xe8uvvx x1ghz6dp x1717udv">
        {rows.map((row, index) => <li key={row.id} {...{
        0: {
          className: "x78zum5 xdt5ytf xskm3c9 xp1t01x x3i8z2n xso031l x1q0q8m5 x1t74ka8"
        },
        1: {
          className: "x78zum5 xdt5ytf xskm3c9 xp1t01x x3i8z2n x1q0q8m5 x1t74ka8 x1qhh985"
        }
      }[!!(index === rows.length - 1) << 0]}>
            <div className="x78zum5 x1a02dak x1qughib x1vdzsm xcfm6fe">
              <span className="xqu410z">
                {row.subject === null ? action(row.action) : `${action(row.action)}, ${row.subject}`}
                <span className="x1jty25b xox936i"> by {row.actorEmail ?? "the system"}</span>
              </span>
              <time dateTime={row.createdAt} className="x1jty25b xuxw1ft x1l1vl4p">
                {moment(row.createdAt)}
              </time>
            </div>
            <p className="x1ghz6dp x1mzt3pk x15g8yj6">{row.reason ?? "No reason recorded"}</p>
          </li>)}
      </ol>
    </Panel>;
}